/**
 * rerunDelta — pure computation of "what has changed since this report was
 * generated" for the Rerun panel landing card (RR1).
 *
 * Inputs come from three places:
 *   - the parent SiteRagReport (what we'd be re-running)
 *   - the owning SiteEntity (carries the live SED version + sedComputedAt)
 *   - the list of provider ids that gained a user/admin key since the
 *     report was generated (computed by the caller from store state)
 *
 * Output is a `RerunDelta` shape consumed by `<RerunDeltaCard />`. The
 * computation is pure and synchronous — no IO. Keep it that way so the
 * card renders synchronously on panel open.
 */
import type {
  SiteRagReport,
  SiteDocument,
  ProjectIntent,
  TypedPreferencesByType,
} from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { SiteContext } from '../types/siteContext';
import type { SedSectionId, SiteEvidenceDocument } from '../types/siteEvidence';
import type { ApiProviderId } from '../config/apiProviders';
import { API_PROVIDERS } from '../config/apiProviders';

export interface RerunDelta {
  /** New documents added to the site/report library since report.generatedAt/createdAt. */
  newDocuments: SiteDocument[];
  /**
   * Difference between the site's current SED version and the version this
   * report was pinned to (`site.sedVersion - report.sedVersionUsed`). Null
   * when either is missing or they're equal.
   */
  sedVersionDrift: number | null;
  /** SED sections whose status improved (verified ← anomalous/unavailable) since v(N). */
  upgradedSections: SedSectionId[];
  /** Number of gaps that were unresolved on v(N) and now resolved. */
  resolvedGaps: number;
  /** Number of gaps still unresolved at panel-open time. Drives Step 1 visibility. */
  pendingGaps: number;
  /**
   * Whether intent/prefs/context differ from the report's saved snapshots.
   * Now a derived OR over the three granular signals below; preserved for
   * back-compat with RerunDeltaCard's existing render path. RDF-1.
   */
  configChanged: boolean;
  /** Providers that gained a key (user-personal or org/env) since v(N). */
  providerKeysAdded: ApiProviderId[];

  // ── RDF-1: granular config-change signals ─────────────────────────────
  /**
   * Live `site.projectIntent` differs from `report.projectIntentAtGen`.
   * Optional — back-compat with consumers/tests that built `RerunDelta`
   * literals before RDF. Treat absent as `false`.
   */
  projectIntentChanged?: boolean;
  /** Live typed preferences differ from `report.typedPreferencesAtGen`. Optional (see above). */
  typedPrefsChanged?: boolean;
  /** Live site context differs from `report.siteContextAtGen`. Optional (see above). */
  siteContextChanged?: boolean;
  /**
   * Compact per-field diff for display in the card. Empty when nothing
   * changed; only populated for project-intent edits (typed-prefs +
   * site-context diffs are summarised at the row level, not field level).
   */
  intentDiffSummary?: Array<{ field: string; from: string; to: string }>;

  // ── RDF-4: Apply-on-Rerun queue signal ────────────────────────────────
  /**
   * Number of `sanityFindings` with `resolution === 'apply'` queued on
   * this report (the Apply-on-Rerun queue). Optional (see RDF-1 rationale).
   */
  appliedFindingsCount?: number;
  /** Truncated preview (first 3) of the queued-finding titles for the card. */
  appliedFindingsPreview?: string[];

  // ── SOC — Site Overview snapshot provenance ───────────────────────────
  /**
   * Provenance of the `*AtGen` site-overview snapshots on this report.
   *
   *   - `'sed'`        — snapshots came from the SED's SOC channels
   *                      (the SED itself is internally consistent with the
   *                      user's overview at SED-compile time).
   *   - `'live-store'` — snapshots came from the live Zustand store at
   *                      report-generation time (legacy path, or the SED
   *                      predated SOC).
   *
   * Surfaced on the delta so the rerun-diff UI can show a caption like
   * "Snapshots: SED compile @ {date}" or "Snapshots: live store @ {date}".
   *
   * Optional — undefined when `report.siteOverviewSnapshotProvenance` is
   * undefined (pre-SOC reports). The card defaults `undefined` to
   * `'live-store'` (historical behaviour).
   */
  siteOverviewSnapshotProvenance?: 'sed' | 'live-store';
}

export interface ComputeRerunDeltaInputs {
  report: SiteRagReport;
  /** Owning site if locatable. Optional — a missing site degrades gracefully. */
  site?: SiteEntity | null;
  /** Provider ids known to have a key configured *now*. Caller derives from store state. */
  configuredProviderIds?: ReadonlyArray<ApiProviderId>;
  /**
   * Provider ids that already had a key at the time the report was generated.
   * If the caller can't reconstruct this (no audit trail), it can pass an
   * empty array — providerKeysAdded then degrades to "what's configured now".
   * We keep it explicit so the card doesn't repeatedly say "key added" for
   * keys that have always been there.
   */
  providerIdsAtReportTime?: ReadonlyArray<ApiProviderId>;
  /**
   * Live project intent — when omitted, falls back to `site.projectIntent`.
   * Caller may pass `null` to force a "no live data" outcome.
   */
  liveProjectIntent?: ProjectIntent | null;
  /** Live typed preferences — falls back to `site.typedPreferencesDefaults`. */
  liveTypedPrefs?: TypedPreferencesByType | null;
  /** Live site context — falls back to `site.siteContext`. */
  liveSiteContext?: SiteContext | null;
  /**
   * Loaded SiteEvidenceDocument — when present, used to compute
   * `resolvedGaps` by diffing pinned `report.sedGaps` against the live
   * SED's gaps. Caller (FeasibilityRerunPanel) hoists the lazy-load.
   * Optional; absent ⇒ `resolvedGaps` degrades to 0 (back-compat).
   */
  liveSed?: SiteEvidenceDocument | null;
}

/**
 * Shallow-equal helper for ProjectIntent — walks all keys present on
 * either side and uses Object.is for primitive comparison. Skips
 * deeply-nested objects (customWeights) by JSON-stringifying them.
 */
function shallowEqualIntent(a: ProjectIntent, b: ProjectIntent): boolean {
  const ar = a as unknown as Record<string, unknown>;
  const br = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(ar), ...Object.keys(br)]);
  for (const k of keys) {
    const av = ar[k];
    const bv = br[k];
    if (Object.is(av, bv)) continue;
    // Object-valued fields (e.g. customWeights) — compare via JSON to
    // avoid spurious diffs from referential inequality.
    if (av && bv && typeof av === 'object' && typeof bv === 'object') {
      if (JSON.stringify(av) === JSON.stringify(bv)) continue;
    }
    return false;
  }
  return true;
}

/**
 * Format a ProjectIntent field value as a short display string for the
 * card's diff list. Objects collapse to JSON, undefined/null to '—'.
 */
function fmtIntentValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Resolve the "report was generated at" timestamp. Falls back to createdAt
 * (always set) when no explicit generatedAt is present.
 */
function reportGenerationTime(report: SiteRagReport): number {
  const raw = (report as unknown as { generatedAt?: string }).generatedAt
    ?? report.createdAt;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

/**
 * SED section statuses considered "good" for the upgrade check. Anything
 * else (anomalous, unavailable, partial, missing) counts as not-verified.
 */
const VERIFIED_STATUSES = new Set<string>([
  'verified',
  'verified-from-doc',
  // UAE-1: a user-authored claim that slots into an SED section as
  // `verified-from-context` carries usable data — treat it as a "good"
  // status for the upgrade check.
  'verified-from-context',
]);

/**
 * Compute the delta between the live state and the report's pinned snapshot.
 *
 * Pure function — no React, no IO. The caller injects state it has read
 * from the store. Returns a fully-populated RerunDelta even when inputs are
 * sparse; consumers can ask `isRerunDeltaEmpty(delta)` to detect the
 * "nothing to do" case.
 */
export function computeRerunDelta(inputs: ComputeRerunDeltaInputs): RerunDelta {
  const { report, site, configuredProviderIds = [], providerIdsAtReportTime = [] } = inputs;

  // ── New documents ──────────────────────────────────────────────────────
  // Anything uploaded after the report generation time. Prefer the site's
  // document library when available (authoritative) but fall back to the
  // report's own attached documents for legacy reports.
  const gen = reportGenerationTime(report);
  const libraryDocs = site?.documents ?? [];
  const reportDocs = report.documents ?? [];
  // Dedupe by id, preferring the site library entry.
  const allById = new Map<string, SiteDocument>();
  for (const d of reportDocs) allById.set(d.id, d);
  for (const d of libraryDocs) allById.set(d.id, d);
  const newDocuments: SiteDocument[] = [];
  for (const doc of allById.values()) {
    const uploaded = Date.parse(doc.uploadedAt);
    if (Number.isFinite(uploaded) && uploaded > gen) newDocuments.push(doc);
  }
  newDocuments.sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));

  // ── SED version drift ──────────────────────────────────────────────────
  const liveSedVersion = site?.sedVersion;
  const pinnedSedVersion = report.sedVersionUsed;
  let sedVersionDrift: number | null = null;
  if (typeof liveSedVersion === 'number' && typeof pinnedSedVersion === 'number') {
    const diff = liveSedVersion - pinnedSedVersion;
    sedVersionDrift = diff !== 0 ? diff : null;
  }

  // ── Upgraded sections ──────────────────────────────────────────────────
  // Section statuses are only available from a loaded SED document. Without
  // that we can't tell which sections improved — surface a conservative
  // empty list and let the caller (which holds the SED) override if needed.
  // The wider RR1 wiring passes upgradedSections through via a thin helper
  // that reads the section statuses from the report's frozen reference
  // counts vs. the live SED — for now we expose the structure and leave the
  // count consistent with the version drift (callers pass the live SED if
  // they have it).
  const upgradedSections: SedSectionId[] = [];
  const liveSections = (site as unknown as {
    sedSectionStatuses?: Partial<Record<SedSectionId, string>>;
  } | undefined)?.sedSectionStatuses;
  const reportSections = (report as unknown as {
    sedSectionStatusesAtGen?: Partial<Record<SedSectionId, string>>;
  }).sedSectionStatusesAtGen;
  if (liveSections && reportSections) {
    for (const [id, status] of Object.entries(liveSections) as Array<[SedSectionId, string]>) {
      if (!VERIFIED_STATUSES.has(status)) continue;
      const prior = reportSections[id];
      if (prior && !VERIFIED_STATUSES.has(prior)) upgradedSections.push(id);
    }
  }

  // ── Provider keys added ────────────────────────────────────────────────
  const prior = new Set(providerIdsAtReportTime);
  const providerKeysAdded = configuredProviderIds.filter(p => !prior.has(p));

  // ── Gap counts ─────────────────────────────────────────────────────────
  // We count gaps on the report itself; the panel later refines this with
  // user actions, but for the landing card we want "as-of-open" numbers.
  const reportGaps = report.sedGaps ?? [];
  const pendingGaps = reportGaps.length;
  // RDF-2 — resolved-since-v(N) is the count of gap entries that were
  // present on the report's pinned `sedGaps` snapshot but are no longer
  // present in the LIVE SED's gaps. We can only compute this when the
  // caller has hoisted the live SED (RDF-3 wiring); otherwise we
  // degrade to 0 (which is correct for "we don't know yet" — the card
  // simply doesn't fire the row). Comparison is by gap string equality
  // since the legacy `sedGaps: string[]` shape is what reports
  // persist; the live SED's typed `gaps[]` collapse to the same
  // `[severity] field: description` form via stringification of the
  // pinned set keyed by descriptor.
  let resolvedGaps = 0;
  const liveSed = inputs.liveSed ?? null;
  if (liveSed) {
    // Build a set of live gap descriptors. The live SED carries
    // `gaps[]` as DataGap objects (severity + field + description +
    // optional sectionId). We collapse to the same string the
    // legacy `sedGaps: string[]` carries so identical gaps match
    // by content.
    const liveGapKeys = new Set<string>();
    const liveGaps = Array.isArray((liveSed as { gaps?: unknown }).gaps)
      ? (liveSed as { gaps: Array<{ field?: string; description?: string; sectionId?: string; impact?: string }> }).gaps
      : [];
    for (const g of liveGaps) {
      const sev = g.impact ?? '';
      const field = g.field ?? g.sectionId ?? '';
      const desc = g.description ?? '';
      liveGapKeys.add(`[${sev}] ${field}: ${desc}`);
      // Also index by description alone in case the report carries a
      // legacy free-text gap that doesn't match the structured form.
      if (desc) liveGapKeys.add(desc);
    }
    for (const pinned of reportGaps) {
      if (typeof pinned !== 'string') continue;
      // A pinned gap is "resolved" when it doesn't appear in the
      // live SED's gap set (i.e. the SED no longer reports the
      // same gap descriptor).
      if (!liveGapKeys.has(pinned)) {
        // Also try the substring fallback so a re-worded gap
        // string still counts as resolved.
        let matched = false;
        for (const k of liveGapKeys) {
          if (pinned.includes(k) || k.includes(pinned)) { matched = true; break; }
        }
        if (!matched) resolvedGaps++;
      }
    }
  }

  // ── RDF-1: granular config-change comparisons ──────────────────────────
  // Read live values from the explicit override → site fallback chain.
  // Read at-gen snapshots from the report (SC2 stamping). Legacy reports
  // without snapshots short-circuit to `false` per the nullish guards,
  // which is the correct degradation: we can't claim "changed" against an
  // absent baseline.
  const liveIntent = inputs.liveProjectIntent !== undefined
    ? inputs.liveProjectIntent
    : site?.projectIntent ?? null;
  const atGenIntent = report.projectIntentAtGen ?? null;
  const projectIntentChanged = !!(
    atGenIntent && liveIntent && !shallowEqualIntent(liveIntent, atGenIntent)
  );

  const intentDiffSummary: Array<{ field: string; from: string; to: string }> = [];
  if (projectIntentChanged && atGenIntent && liveIntent) {
    const fromR = atGenIntent as unknown as Record<string, unknown>;
    const toR = liveIntent as unknown as Record<string, unknown>;
    const keys = new Set<string>([
      ...Object.keys(fromR),
      ...Object.keys(toR),
    ]);
    for (const key of keys) {
      const fromV = fromR[key];
      const toV = toR[key];
      if (Object.is(fromV, toV)) continue;
      // Object fields: skip when JSON-equal (matches shallowEqualIntent)
      if (
        fromV && toV && typeof fromV === 'object' && typeof toV === 'object'
        && JSON.stringify(fromV) === JSON.stringify(toV)
      ) continue;
      intentDiffSummary.push({
        field: key,
        from: fmtIntentValue(fromV),
        to: fmtIntentValue(toV),
      });
    }
  }

  const livePrefs = inputs.liveTypedPrefs !== undefined
    ? inputs.liveTypedPrefs
    : site?.typedPreferencesDefaults ?? null;
  const atGenPrefs = report.typedPreferencesAtGen ?? null;
  const typedPrefsChanged = !!(
    atGenPrefs && livePrefs && JSON.stringify(livePrefs) !== JSON.stringify(atGenPrefs)
  );

  const liveCtx = inputs.liveSiteContext !== undefined
    ? inputs.liveSiteContext
    : site?.siteContext ?? null;
  const atGenCtx = report.siteContextAtGen ?? null;
  const siteContextChanged = !!(
    atGenCtx && liveCtx && (
      (liveCtx.text ?? '').trim() !== (atGenCtx.text ?? '').trim()
      || (liveCtx.claims?.length ?? 0) !== (atGenCtx.claims?.length ?? 0)
    )
  );

  // RDF-1 — derived OR over the three granular signals. Back-compat with
  // existing `configChanged` consumers (e.g. RerunDeltaCard).
  const configChanged = projectIntentChanged || typedPrefsChanged || siteContextChanged;

  // ── RDF-4: Apply-on-Rerun queue ────────────────────────────────────────
  // Count `sanityFindings` where `resolution === 'apply'`. These are
  // queued for the next rerun's narrative builders (Apply_On_Rerun_Queue
  // pack semantics). Surface the count + the first three titles for the
  // card preview.
  const sanityFindings = report.sanityFindings ?? [];
  const applyQueue = sanityFindings.filter(f => f.resolution === 'apply');
  const appliedFindingsCount = applyQueue.length;
  const appliedFindingsPreview = applyQueue.slice(0, 3).map(f =>
    (typeof f.finding === 'string' && f.finding.trim().length > 0)
      ? f.finding
      : (f.id ?? '(unnamed finding)'),
  );

  // SOC — surface the snapshot-provenance discriminator on the delta so
  // the rerun-diff card can render "Snapshots: SED compile" vs
  // "Snapshots: live store". Pre-SOC reports default `undefined`.
  const siteOverviewSnapshotProvenance =
    (report as unknown as { siteOverviewSnapshotProvenance?: 'sed' | 'live-store' })
      .siteOverviewSnapshotProvenance;

  return {
    newDocuments,
    sedVersionDrift,
    upgradedSections,
    resolvedGaps,
    pendingGaps,
    configChanged,
    providerKeysAdded,
    projectIntentChanged,
    typedPrefsChanged,
    siteContextChanged,
    intentDiffSummary,
    appliedFindingsCount,
    appliedFindingsPreview,
    siteOverviewSnapshotProvenance,
  };
}

/**
 * True when the delta has nothing actionable. The card uses this to flip
 * to the "Nothing has changed" copy + disabled Run button.
 *
 * RDF-1/2/4: also flips back to false the moment any of the new granular
 * signals fires (projectIntentChanged / typedPrefsChanged /
 * siteContextChanged / appliedFindingsCount > 0). The `configChanged`
 * field is a derived OR over the first three so checking either the
 * roll-up or the granular fields suffices — we check both for paranoia
 * (a hand-crafted RerunDelta literal might set only the roll-up).
 */
export function isRerunDeltaEmpty(delta: RerunDelta): boolean {
  return (
    delta.newDocuments.length === 0
    && (delta.sedVersionDrift == null || delta.sedVersionDrift === 0)
    && delta.upgradedSections.length === 0
    && delta.resolvedGaps === 0
    && delta.providerKeysAdded.length === 0
    && !delta.configChanged
    && !delta.projectIntentChanged
    && !delta.typedPrefsChanged
    && !delta.siteContextChanged
    && (delta.appliedFindingsCount ?? 0) === 0
  );
}

/** Human label for a provider id — wraps API_PROVIDERS lookup with a fallback. */
export function providerLabel(id: ApiProviderId): string {
  return API_PROVIDERS[id]?.displayName ?? id;
}
